import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Copy, Check, X } from 'lucide-react';
import { useToast } from '@/shared/components/ui/use-toast';
import { useRecoilState } from 'recoil';
import { workflowNodesState } from '../state/workflowState';
import { DOMAIN_CONFIG } from '@/infrastructure/config/domain';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/shared/components/ui/alert-dialog';

interface WebhookWaitingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  nodeId: string;
  webhookUrl: string;
  fullUrl?: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onWebhookData: (data: any) => void;
  onWebhookTimeout: (nodeId: string) => void;
  onStopWorkflow?: () => void; 
}

const WebhookWaitingDialog: React.FC<WebhookWaitingDialogProps> = ({
  isOpen,
  onClose,
  nodeId,
  webhookUrl,
  fullUrl,
  onWebhookData: _onWebhookData, // Renomeando para indicar que não é usado
  onWebhookTimeout,
  onStopWorkflow,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const INITIAL_TIMEOUT = 300; 
  const [elapsedTime, setElapsedTime] = useState(INITIAL_TIMEOUT);
  const [error, setError] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);
  const [, setNodes] = useRecoilState(workflowNodesState);
  
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const [globalNodeId] = useState(nodeId);

  useEffect(() => {
    console.log('WebhookWaitingDialog recebeu props:', {
      isOpen,
      nodeId,
      webhookUrl,
      fullUrl,
      globalNodeId
    });
  }, [isOpen, nodeId, webhookUrl, fullUrl, globalNodeId]);

  const updateNodeLoading = (nodeId: string, isLoading: boolean) => {
    setNodes((currentNodes) =>
      currentNodes.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, isLoading: isLoading } }
          : node
      )
    );
  };

  const getFullWebhookUrl = () => {
    // Se fullUrl existe, extrair apenas o path (mantendo IDs) e construir com domínio frontend
    if (fullUrl) {
      try {
        const url = new URL(fullUrl);
        let webhookPath = url.pathname; // Extrai apenas o path com IDs
        
        // Remover prefixos para evitar duplicação
        if (webhookPath.startsWith('/api/webhook')) {
          webhookPath = webhookPath.replace('/api/webhook', '');
        } else if (webhookPath.startsWith('/webhook')) {
          webhookPath = webhookPath.replace('/webhook', '');
        }
        
        return DOMAIN_CONFIG.buildFrontendWebhookUrl(webhookPath);
      } catch (error) {
        console.warn('Erro ao processar fullUrl, usando webhookUrl como fallback:', error);
      }
    }
    
    if (!webhookUrl) {
      console.warn('webhookUrl está indefinida, usando uma URL padrão');
      return DOMAIN_CONFIG.buildFrontendWebhookUrl('/undefined');
    }
    
    // Usar configuração do domínio frontend
    const path = webhookUrl.startsWith('/') ? webhookUrl : `/${webhookUrl}`;
    
    return DOMAIN_CONFIG.buildFrontendWebhookUrl(path);
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isOpen && nodeId) {
      
      setElapsedTime(INITIAL_TIMEOUT);
      setError(null);
      
      console.log('Diálogo de espera de webhook aberto para o node:', nodeId);

      
      timer = setInterval(() => {
        setElapsedTime((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setError(t('workflow.webhook.error.timeout'));
            onWebhookTimeout(nodeId);
            onClose();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      updateNodeLoading(nodeId, true);
    }

    return () => {
      clearInterval(timer);
      if (nodeId) {
        updateNodeLoading(nodeId, false);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, nodeId, onClose, t, onWebhookTimeout]);


  const handleCloseRequest = () => {
    console.log('Fechamento do diálogo solicitado');
    
    if (onStopWorkflow) {
      console.log('Abrindo diálogo de confirmação para parar workflow');
      setIsConfirmDialogOpen(true);
    } else {
      console.log('Fechando diálogo sem confirmação (sem onStopWorkflow)');
      onClose();
    }
  };

  
  const handleConfirmStop = () => {
    console.log('Confirmação para parar workflow recebida');
    setIsConfirmDialogOpen(false);
    
    if (onStopWorkflow) {
      console.log('Chamando onStopWorkflow para encerrar o fluxo');
      onStopWorkflow();
    }
    
    console.log('Fechando diálogo após confirmação');
    onClose();
  };

  
  const handleCancelClose = () => {
    console.log('Fechamento cancelado pelo usuário');
    setIsConfirmDialogOpen(false);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formattedWebhookUrl = getFullWebhookUrl();

  
  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(formattedWebhookUrl);
      setCopying(true);
      setTimeout(() => setCopying(false), 2000);
      toast({
        description: t('workflow.test.copied_to_clipboard'),
      });
    } catch (err) {
      setError(t('workflow.test.copy_error') || 'Erro ao copiar URL');
      console.error('Erro ao copiar para o clipboard:', err);
    }
  };

  return (
    <>
      <Dialog 
        open={isOpen} 
        onOpenChange={(open) => {
          if (!open) {
            console.log('🔴 Dialog recebeu sinal para fechar! Usando handleCloseRequest em vez de fechamento direto');
            handleCloseRequest();
            return false;
          }
          return true;
        }}
      >
        <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-webhook-waiting-node={nodeId}>
          <button 
            onClick={handleCloseRequest}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
          
          <div className="pt-6 px-6">
            <h2 className="text-lg font-semibold text-center">{t('workflow.webhook.waiting_title')}</h2>
          </div>
          
          <div className="flex flex-col items-center space-y-4 p-6 pt-4">
            {error ? (
              <div className="flex items-center p-4 bg-red-100 rounded-md text-red-700 w-full">
                <AlertCircle className="h-5 w-5 mr-2" />
                <span>{error}</span>
              </div>
            ) : (
              <>
                
                <div className="text-blue-500 mb-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-12 w-12 text-primary opacity-80 animate-pulse">
                    <line x1="10" x2="14" y1="2" y2="2"></line>
                    <line x1="12" x2="15" y1="14" y2="11"></line>
                    <circle cx="12" cy="14" r="8"></circle>
                  </svg>
                </div>
                
                
                <div className="text-xl font-semibold -mt-1">
                  {formatTime(elapsedTime)}
                </div>
                
                
                <p className="text-center text-muted-foreground">
                  {t('workflow.webhook.waiting_message_simple')}
                </p>
                
                
                <div className="w-full mt-2">
                  <h4 className="text-xs uppercase text-muted-foreground tracking-wider mb-2 text-center font-normal">
                    {t('workflow.webhook.url')}
                  </h4>
                  
                  <div className="flex items-center">
                    <input
                      className="flex-1 p-2 border rounded-l-md text-sm bg-background"
                      value={formattedWebhookUrl}
                      readOnly
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-l-none h-[38px]"
                      onClick={copyWebhookUrl}
                    >
                      {copying ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('workflow.webhook.url_help')}
                  </p>
                </div>
              </>
            )}
          </div>
          
          
          <div className="flex justify-end p-3 bg-muted border-t">
            <Button variant="outline" onClick={handleCloseRequest}>
              {t('common.actions.cancel')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      
      <AlertDialog open={isConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('workflow.webhook.confirm_stop_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('workflow.webhook.confirm_stop_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>
              {t('common.actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmStop} className="bg-red-600 hover:bg-red-700">
              {t('workflow.webhook.stop_workflow')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default WebhookWaitingDialog;
